import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, BookOpen, Plus, Users2, ClipboardList, GraduationCap,
  Award, Users, Image as ImageIcon, ShieldCheck, FileText, DollarSign,
  UserCircle, LifeBuoy, LogOut, ExternalLink,
} from "lucide-react";

const NAV_SECTIONS = [
  { title: "Overview", items: [
    { to: "/partner/dashboard", label: "Overview", icon: LayoutDashboard, testid: "partner-nav-overview" },
  ]},
  { title: "Courses", items: [
    { to: "/partner/courses",   label: "My Courses", icon: BookOpen,       testid: "partner-nav-courses" },
    { to: "/partner/courses/new", label: "Add Course", icon: Plus,          testid: "partner-nav-add-course" },
    { to: "/partner/cohorts",   label: "Cohorts",    icon: Users2,         testid: "partner-nav-cohorts" },
  ]},
  { title: "Students", items: [
    { to: "/partner/students",     label: "Students",     icon: Users,          testid: "partner-nav-students" },
    { to: "/partner/registrations", label: "Registrations", icon: ClipboardList, testid: "partner-nav-registrations" },
    { to: "/partner/assessments",   label: "Assessments",  icon: GraduationCap, testid: "partner-nav-assessments" },
    { to: "/partner/certificates",  label: "Certificates", icon: Award,         testid: "partner-nav-certificates" },
  ]},
  { title: "Organization", items: [
    { to: "/partner/trainers",       label: "Trainers",       icon: Users,        testid: "partner-nav-trainers" },
    { to: "/partner/media",          label: "Media",          icon: ImageIcon,    testid: "partner-nav-media" },
    { to: "/partner/accreditations", label: "Accreditations", icon: ShieldCheck,  testid: "partner-nav-accreditations" },
    { to: "/partner/documents",      label: "Documents",      icon: FileText,     testid: "partner-nav-documents" },
  ]},
  { title: "Business", items: [
    { to: "/partner/sales",   label: "Sales",   icon: DollarSign, testid: "partner-nav-sales" },
    { to: "/partner/profile", label: "Profile", icon: UserCircle, testid: "partner-nav-profile" },
    { to: "/partner/support", label: "Support", icon: LifeBuoy,   testid: "partner-nav-support" },
  ]},
];

export default function PartnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [partner, setPartner] = useState(null);

  const load = () =>
    api.get("/api/partner/me")
       .then(({ data }) => setPartner(data))
       .catch((e) => {
         if (e?.response?.status === 403) navigate("/onboarding/partner");
       });

  useEffect(() => {
    if (!user) return;
    if (user.role !== "partner") { navigate("/"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return <div className="p-12 text-center">Please sign in</div>;
  if (!partner) return <div className="p-12 text-center">Loading partner profile…</div>;

  const isActive = (to) => loc.pathname === to || (to !== "/partner/dashboard" && loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex bg-[#F7F5F0]">
      <aside className="w-64 bg-[#064E3B] text-white flex flex-col shrink-0" data-testid="partner-sidebar">
        <div className="p-5 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/60">Partner</div>
          <div className="font-bold text-base truncate" data-testid="partner-org-name">{partner.organization_name}</div>
          <Badge className={`mt-2 text-[10px] capitalize ${
            partner.status === "approved" ? "bg-emerald-500/25 text-emerald-100"
            : partner.status === "changes_requested" ? "bg-amber-400/25 text-amber-100"
            : "bg-white/10 text-white/80"}`}>
            {partner.status?.replace("_", " ")}
          </Badge>
          {partner.status !== "approved" && (
            <Link to="/onboarding/partner"
              data-testid="partner-finish-onboarding-link"
              className="mt-3 block text-[11px] text-amber-200 hover:text-amber-100 underline">
              Complete onboarding →
            </Link>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4 text-sm">
          {NAV_SECTIONS.map((sec) => (
            <div key={sec.title}>
              <div className="text-[9px] uppercase tracking-widest text-white/40 px-2 mb-1">{sec.title}</div>
              <div className="space-y-0.5">
                {sec.items.map((l) => {
                  const Icon = l.icon;
                  const active = isActive(l.to);
                  return (
                    <Link key={l.to} to={l.to}
                      data-testid={l.testid}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] transition-colors ${
                        active ? "bg-white/15 font-semibold" : "hover:bg-white/10 text-white/85"}`}>
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{l.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-1.5">
          <a href={`/partners/${partner.slug}`} target="_blank" rel="noreferrer"
            data-testid="partner-view-storefront"
            className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white">
            <ExternalLink className="w-3 h-3" /> View storefront
          </a>
          <Link to="/" className="block text-xs text-white/60 hover:text-white"
            data-testid="partner-back-home">← Back to Teacher4Me</Link>
          <button onClick={logout} className="text-xs text-white/60 hover:text-white flex items-center gap-1"
            data-testid="partner-nav-logout">
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-8 overflow-y-auto min-w-0">
        <Outlet context={{ partner, refreshPartner: load }} />
      </main>
    </div>
  );
}
